import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getTeamSchedule } from "./api";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const styles = {
    container: {
        padding: "0 24px",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
    },
    tableWrapper: {
        overflowX: "auto",
        backgroundColor: "var(--bg-card)",
        borderRadius: "16px",
        border: "1px solid var(--border-color)",
        boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
        paddingBottom: "10px",
    },
    table: {
        width: "max-content",
        minWidth: "100%",
        borderCollapse: "separate",
        borderSpacing: 0,
        fontSize: "13px",
    },
    th: {
        padding: "16px 12px",
        backgroundColor: "var(--bg-header)",
        color: "var(--text-muted)",
        borderBottom: "1px solid var(--border-color)",
        textAlign: "center",
        verticalAlign: "middle",
        position: "sticky",
        top: 0,
        zIndex: 5,
        borderRight: "1px dashed var(--border-color)",
    },
    tdName: {
        padding: "12px 20px",
        backgroundColor: "var(--bg-card)",
        borderBottom: "1px solid var(--border-color)",
        borderRight: "1px solid var(--border-color)",
        fontWeight: "600",
        position: "sticky",
        left: 0,
        zIndex: 10,
        minWidth: "260px",
        color: "var(--text-main)",
        boxShadow: "4px 0 15px -4px rgba(0,0,0,0.05)",
        transition: "background-color 0.2s ease",
    },
    cell: {
        padding: "8px 6px",
        borderBottom: "1px solid var(--border-color)",
        borderRight: "1px dashed var(--border-color)",
        textAlign: "center",
        height: "64px",
        verticalAlign: "middle",
        transition: "background-color 0.2s ease",
        backgroundColor: "var(--bg-card)",
    },
    loadingOverlay: {
        padding: "120px",
        textAlign: "center",
        color: "var(--text-muted)",
        fontSize: "18px",
        fontWeight: "500",
    }
};

const AZ_MONTHS = [
    "Yanvar", "Fevral", "Mart", "Aprel", "May", "İyun",
    "İyul", "Avqust", "Sentyabr", "Oktyabr", "Noyabr", "Dekabr"
];

const AZ_DAYS_SHORT = ["B.", "B.e.", "Ç.a.", "Ç.", "C.a.", "C.", "Ş."];

const formatDateLocal = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export default function TeamCalendar() {
    const [teamData, setTeamData] = useState([]);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 600);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const [view, setView] = useState("week");
    const [currentDate, setCurrentDate] = useState(() => {
        const today = new Date();
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(today);
        monday.setDate(diff);
        return monday;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchTeam = useCallback(async (targetDate, targetView, searchVal) => {
        console.log(`[TeamCalendar] Fetching data for ${targetView} starting ${formatDateLocal(targetDate)} with search: ${searchVal}`);
        setTeamData([]);
        setLoading(true);
        setError("");

        try {
            const start = new Date(targetDate);
            const end = new Date(targetDate);

            if (targetView === "week") {
                end.setDate(start.getDate() + 6);
            } else if (targetView === "month") {
                end.setMonth(start.getMonth() + 1);
                end.setDate(0);
            } else if (targetView === "quarter") {
                end.setMonth(start.getMonth() + 3);
                end.setDate(0);
            }

            const res = await getTeamSchedule(formatDateLocal(start), formatDateLocal(end), searchVal || "");
            setTeamData(res.data || []);
        } catch (e) {
            console.error("[TeamCalendar] Fetch Error:", e);
            setError("Məlumat yüklənərkən xəta baş verdi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTeam(currentDate, view, debouncedSearch);
    }, [currentDate, view, debouncedSearch, fetchTeam]);

    const days = useMemo(() => {
        const arr = [];
        const start = new Date(currentDate);
        let count = 7;

        if (view === "month") {
            count = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
        } else if (view === "quarter") {
            const endQ = new Date(start.getFullYear(), start.getMonth() + 3, 0);
            const diffTime = Math.abs(endQ - start);
            count = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        }

        for (let i = 0; i < count; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            arr.push(d);
        }
        return arr;
    }, [currentDate, view]);

    const handleNav = (dir) => {
        const next = new Date(currentDate);
        if (view === "week") {
            next.setDate(next.getDate() + (dir * 7));
        } else if (view === "month") {
            next.setMonth(next.getMonth() + dir);
            next.setDate(1);
        } else if (view === "quarter") {
            next.setMonth(next.getMonth() + (dir * 3));
            next.setDate(1);
        }
        setCurrentDate(next);
    };

    const handleViewChange = (newView) => {
        let next = new Date(currentDate.getTime());
        const today = new Date();

        if (newView === "month") {
            if (view === "week") {
                next.setDate(next.getDate() + 3);
            } else if (view === "quarter") {
                const qStartMonth = Math.floor(next.getMonth() / 3) * 3;
                if (today.getFullYear() === next.getFullYear() &&
                    today.getMonth() >= qStartMonth && today.getMonth() < qStartMonth + 3) {
                    next = new Date(today.getTime());
                }
            }
            next.setDate(1);
        } else if (newView === "quarter") {
            const qStartMonth = Math.floor(next.getMonth() / 3) * 3;
            next.setMonth(qStartMonth);
            next.setDate(1);
        } else if (newView === "week") {
            if (view === "month") {
                next.setDate(2);
            } else if (view === "quarter") {
                const qStartMonth = Math.floor(next.getMonth() / 3) * 3;
                if (today.getFullYear() === next.getFullYear() &&
                    today.getMonth() >= qStartMonth && today.getMonth() < qStartMonth + 3) {
                    next = new Date(today.getTime());
                } else {
                    next.setDate(2);
                }
            }
            const day = next.getDay();
            const diff = next.getDate() - day + (day === 0 ? -6 : 1);
            next.setDate(diff);
        }

        setView(newView);
        setCurrentDate(next);
    };

    const headerTitle = useMemo(() => {
        if (!days.length) return "Yüklənir...";
        if (view === "month") {
            return `${currentDate.getFullYear()} ${AZ_MONTHS[currentDate.getMonth()]}`;
        }
        if (view === "quarter") {
            const q = Math.floor(currentDate.getMonth() / 3) + 1;
            return `${currentDate.getFullYear()} - Q${q} Rüb`;
        }
        const first = days[0];
        const last = days[days.length - 1];
        return `${first.getDate()} ${AZ_MONTHS[first.getMonth()]} - ${last.getDate()} ${AZ_MONTHS[last.getMonth()]} ${last.getFullYear()}`;
    }, [view, currentDate, days]);

    const exportToExcel = (format = "expanded") => {
        if (!teamData || teamData.length === 0) {
            setError("Eksport etmək üçün məlumat tapılmadı.");
            return;
        }

        // format: "expanded" (Geniş) və ya "hr" (HR Standartı)
        const isNightShift = (info) => {
            const s = (info && info.shift_name) ? info.shift_name.toLowerCase() : "";
            const k = (info && info.shift_key) ? String(info.shift_key).toLowerCase() : "";
            return s.includes("gecə") || s.includes("gece") || k.includes("night") || k.includes("gece");
        };

        const expandedData = teamData.map(user => {
            const row = {
                "İşçi": user.name,
                "ID": `EMP-${user.id}`
            };
            days.forEach(d => {
                const dateStr = formatDateLocal(d);
                const dayInfo = user.days.find(day => day.date === dateStr);
                const columnHeader = d.toLocaleDateString("az-AZ", { day: "numeric", month: "short" });

                if (!dayInfo) {
                    row[columnHeader] = "-";
                } else {
                    const isFact = dayInfo.fact_hours > 0;
                    const isWork = dayInfo.plan === "WORK";

                    let cellVal = "";
                    if (isFact) {
                        const shiftInfo = dayInfo.shift_name ? ` (${dayInfo.shift_name})` : "";
                        cellVal = `İcra edildi (P: ${isWork ? 8 : 0} saat, F: ${dayInfo.fact_hours} saat)${shiftInfo}`;
                    } else if (isWork) {
                        cellVal = "İş (8 saat)";
                    } else {
                        cellVal = "İstirahət (0 saat)";
                    }
                    row[columnHeader] = cellVal;
                }
            });
            return row;
        });

        const hrData = teamData.map(user => {
            const row = {
                "İşçi": user.name,
                "ID": `EMP-${user.id}`
            };
            days.forEach(d => {
                const dateStr = formatDateLocal(d);
                const dayInfo = user.days.find(day => day.date === dateStr);
                const header = `${d.getDate()} ${AZ_MONTHS[d.getMonth()].toLocaleLowerCase("az-AZ")} ${d.getFullYear()}`;

                if (!dayInfo) {
                    row[header] = "";
                } else {
                    const isWork = dayInfo.plan === "WORK" || dayInfo.fact_hours > 0;
                    if (!isWork) {
                        row[header] = "i";
                    } else {
                        row[header] = isNightShift(dayInfo) ? 2 : 1;
                    }
                }
            });
            return row;
        });

        const workbook = XLSX.utils.book_new();

        let sheetName = "Komanda_Cədvəli";
        if (view === "month") sheetName = `${currentDate.getFullYear()}_Ayliq`;
        else if (view === "quarter") sheetName = `${currentDate.getFullYear()}_Q${Math.floor(currentDate.getMonth() / 3) + 1}`;
        else sheetName = `${currentDate.getFullYear()}_Həftəlik`;

        let suffix;
        if (format === "hr") {
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(hrData), sheetName.slice(0, 21) + "_HR");
            suffix = "HR";
        } else {
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(expandedData), sheetName.slice(0, 25) + "_Genis");
            suffix = "Genis";
        }
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

        const fileData = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
        saveAs(fileData, `Komanda_Cədvəli_${suffix}_${view}_${formatDateLocal(new Date())}.xlsx`);
        setExportMenuOpen(false);
    };

    return (
        <div style={styles.container}>
            <div className="card-container" style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <button className="btn btn-icon" onClick={() => handleNav(-1)}>⟨</button>
                    <button className="btn" onClick={() => {
                        const today = new Date();
                        today.setDate(1);
                        setCurrentDate(today);
                    }}>Bu Ay</button>
                    <button className="btn btn-icon" onClick={() => handleNav(1)}>⟩</button>
                    <span style={{ marginLeft: "15px", fontWeight: "700", fontSize: "18px", color: "var(--text-main)" }}>
                        {headerTitle}
                    </span>
                    <button
                        className="btn animate-fade-in"
                        style={{ marginLeft: "15px", color: "var(--primary-color)", borderColor: "var(--primary-color)" }}
                        onClick={() => fetchTeam(currentDate, view, debouncedSearch)}
                    >🔄 Yenilə</button>
                    <div style={{ position: "relative", marginLeft: "10px" }}>
                        <button
                            className="btn animate-fade-in"
                            style={{ backgroundColor: "#15803d", color: "white", borderColor: "#15803d" }}
                            onClick={() => setExportMenuOpen(o => !o)}
                            title="Excel formatını seçin"
                        >
                            <span>📥</span> Excel ▾
                        </button>
                        {exportMenuOpen && (
                            <>
                                <div
                                    onClick={() => setExportMenuOpen(false)}
                                    style={{ position: "fixed", inset: 0, zIndex: 50 }}
                                />
                                <div style={{
                                    position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 51,
                                    backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)",
                                    borderRadius: "10px", boxShadow: "0 10px 30px -10px rgba(0,0,0,0.25)",
                                    minWidth: "220px", overflow: "hidden"
                                }}>
                                    <button
                                        onClick={() => exportToExcel("expanded")}
                                        style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", color: "var(--text-main)", fontSize: "13px" }}
                                    >📋 Geniş Formada Excel</button>
                                    <div style={{ height: "1px", backgroundColor: "var(--border-color)" }} />
                                    <button
                                        onClick={() => exportToExcel("hr")}
                                        style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", color: "var(--text-main)", fontSize: "13px" }}
                                    >🏢 HR Standartında Excel</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <div style={{ position: "relative" }}>
                        <span style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", opacity: 0.5, fontSize: "15px" }}>🔍</span>
                        <input
                            type="text"
                            placeholder="Ad və ya ID ilə axtar..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input-wow"
                            style={{
                                padding: "10px 14px 10px 40px", borderRadius: "10px", border: "1px solid var(--border-color)",
                                backgroundColor: "var(--bg-card)", color: "var(--text-main)", fontSize: "14px", width: "240px",
                                outline: "none", transition: "all 0.3s ease", boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)"
                            }}
                        />
                    </div>
                    <div style={{ display: "flex", gap: "8px", backgroundColor: "var(--inactive-btn-bg)", padding: "4px", borderRadius: "10px", border: "1px solid var(--border-color)" }}>
                        <button
                            className={`btn ${view === "week" ? "active" : ""}`}
                            style={{ border: "none", boxShadow: view === "week" ? "var(--shadow-sm)" : "none" }}
                            onClick={() => handleViewChange("week")}
                        >Həftə</button>
                        <button
                            className={`btn ${view === "month" ? "active" : ""}`}
                            style={{ border: "none", boxShadow: view === "month" ? "var(--shadow-sm)" : "none" }}
                            onClick={() => handleViewChange("month")}
                        >Ay</button>
                        <button
                            className={`btn ${view === "quarter" ? "active" : ""}`}
                            style={{ border: "none", boxShadow: view === "quarter" ? "var(--shadow-sm)" : "none" }}
                            onClick={() => handleViewChange("quarter")}
                        >Rüb</button>
                    </div>
                </div>
            </div>

            {error && <div style={{ color: "var(--danger-text)", padding: "15px", textAlign: "center", backgroundColor: "var(--danger-bg)", border: "1px solid var(--danger-border)", borderRadius: "var(--border-radius-base)", marginBottom: "24px" }}>⚠️ {error}</div>}

            <div style={styles.tableWrapper} className="animate-slide-down">
                {loading ? (
                    <div style={styles.loadingOverlay}>
                        <div style={{ fontSize: "24px", marginBottom: "10px" }}>⏳</div>
                        Məlumatlar yüklənir, zəhmət olmasa gözləyin...
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, minWidth: "220px", left: 0, zIndex: 11, backgroundColor: "var(--bg-header)" }}>İşçi Məlumatı</th>
                                {days.map(d => {
                                    const todayStr = formatDateLocal(new Date());
                                    const isToday = formatDateLocal(d) === todayStr;
                                    return (
                                        <th key={d.getTime()} style={{
                                            ...styles.th,
                                            minWidth: view === "quarter" ? "80px" : "120px",
                                            backgroundColor: isToday ? "var(--work-bg)" : "var(--bg-header)",
                                        }}>
                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                                <div style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>
                                                    {AZ_DAYS_SHORT[d.getDay()]}
                                                </div>
                                                <div style={{
                                                    fontSize: "18px",
                                                    color: isToday ? "white" : "var(--text-main)",
                                                    fontWeight: "800",
                                                    backgroundColor: isToday ? "var(--primary-color)" : "transparent",
                                                    borderRadius: isToday ? "8px" : "0",
                                                    padding: isToday ? "2px 10px" : "0",
                                                    boxShadow: isToday ? "0 4px 10px rgba(37,99,235,0.3)" : "none"
                                                }}>
                                                    {d.getDate()}
                                                </div>
                                                {(view === "quarter" && d.getDate() === 1) && <div style={{ fontSize: "10px", color: "var(--primary-color)", fontWeight: "700", textTransform: "uppercase" }}>{AZ_MONTHS[d.getMonth()].slice(0, 3)}</div>}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {teamData.length === 0 ? (
                                <tr>
                                    <td colSpan={days.length + 1} style={{ padding: "80px", textAlign: "center", color: "var(--text-muted)" }}>
                                        <div style={{ fontSize: "32px", opacity: 0.5, marginBottom: "12px" }}>📭</div>
                                        {searchQuery ? `"${searchQuery}" sorğusuna uyğun işçi tapılmadı.` : "Seçilmiş dövr üçün heç bir məlumat (plan və ya fakt) tapılmadı."}
                                    </td>
                                </tr>
                            ) : (
                                teamData.map(user => {
                                    // Cari görünüşdəki cəmi saatları hesablayaq
                                    let currentPeriodHours = 0;
                                    days.forEach(d => {
                                        const dateStr = formatDateLocal(d);
                                        const dayInfo = user.days.find(day => day.date === dateStr);
                                        if (dayInfo) currentPeriodHours += (dayInfo.fact_hours || 0);
                                    });

                                    let limit = 0;
                                    if (view === "month") limit = 160;
                                    if (view === "quarter") limit = 480;
                                    if (view === "week") limit = 40;

                                    const isExceeded = limit > 0 && currentPeriodHours > limit;
                                    // Üstələyibsə qırmızı, yoxsa açıq göy tonu (#58a6ff)
                                    const textColor = isExceeded ? "#f85149" : "#58a6ff";

                                    return (
                                        <tr key={user.id} className="team-row">
                                            <td style={styles.tdName} className="sticky-name">
                                                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                                                    <div className="employee-avatar" style={{
                                                        width: "36px", height: "36px", borderRadius: "10px",
                                                        backgroundColor: isExceeded ? "var(--danger-bg)" : "var(--inactive-btn-bg)", display: "flex",
                                                        alignItems: "center", justifyContent: "center", color: textColor,
                                                        fontWeight: "800", fontSize: "14px", border: `1px solid ${isExceeded ? "var(--danger-border)" : "var(--border-color)"}`,
                                                        boxShadow: isExceeded ? "0 4px 10px rgba(248,81,73,0.2)" : "0 2px 5px rgba(0,0,0,0.05)"
                                                    }}>
                                                        {user.name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <div style={{ color: textColor, fontSize: "14px", fontWeight: "700", letterSpacing: "-0.2px" }}>
                                                            {user.name}
                                                            {isExceeded && <span style={{ fontSize: "12px", marginLeft: "6px" }} title="Limit aşılıb">⚠️</span>}
                                                        </div>
                                                        <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px", fontWeight: "500" }}>
                                                            {view !== "week" && <span style={{ padding: "2px 6px", borderRadius: "10px", backgroundColor: isExceeded ? "var(--danger-bg)" : "var(--inactive-btn-bg)", color: isExceeded ? "#f85149" : "inherit" }}>{currentPeriodHours} saat / {limit} saat</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>                                        {days.map(d => {
                                                const dateStr = formatDateLocal(d);
                                                const dayInfo = user.days.find(day => day.date === dateStr);

                                                if (!dayInfo) return <td key={dateStr} style={{ ...styles.cell, backgroundColor: "var(--bg-card)" }}></td>;

                                                const isFact = dayInfo.fact_hours > 0;
                                                const isWork = dayInfo.plan === "WORK";

                                                const isDayShift = dayInfo.shift_key === "day";
                                                const isNightShift = dayInfo.shift_key === "night";

                                                let innerBg = "var(--danger-bg)";
                                                let innerColor = "var(--danger-text)";
                                                let innerBorder = "1px solid transparent";
                                                let titleText = "İST";

                                                if (isFact) {
                                                    if (isNightShift) {
                                                        innerBg = "#1e1b4b"; // Dark Indigo for Night
                                                        innerColor = "#a5b4fc";
                                                        innerBorder = "1px solid #3730a3";
                                                        titleText = "🌙 GECƏ";
                                                    } else if (isDayShift) {
                                                        innerBg = "#f0fdfa"; // Light Teal/Green for Day
                                                        innerColor = "#0f766e";
                                                        innerBorder = "1px solid #ccfbf1";
                                                        titleText = "☀️ GÜNDÜZ";
                                                    } else {
                                                        innerBg = "var(--success-bg)";
                                                        innerColor = "var(--success-text)";
                                                        innerBorder = "1px solid var(--success-border)";
                                                        titleText = "İCRA";
                                                    }
                                                } else if (isWork) {
                                                    innerBg = "var(--work-bg)";
                                                    innerColor = "var(--work-text)";
                                                    titleText = "İŞ";
                                                }

                                                return (
                                                    <td key={dateStr} style={{ ...styles.cell, minWidth: view === "quarter" ? "80px" : "120px" }}>
                                                        <div className="status-pill" style={{
                                                            backgroundColor: innerBg,
                                                            color: innerColor,
                                                            border: innerBorder,
                                                            borderRadius: "12px",
                                                            height: "100%",
                                                            minHeight: "48px",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            padding: "6px",
                                                            boxShadow: "inset 0 1px 2px rgba(255,255,255,0.1)",
                                                        }}>
                                                            <div style={{ fontWeight: 800, fontSize: "11px", letterSpacing: "0.5px" }}>
                                                                {titleText}
                                                            </div>
                                                            <div style={{ display: "flex", gap: "8px", marginTop: "4px", opacity: 0.9, alignItems: "center" }}>
                                                                <div style={{ fontSize: "11px", fontWeight: "600" }}>
                                                                    P:{isWork ? "8" : "0"} saat
                                                                </div>
                                                                {isFact && (
                                                                    <div title={dayInfo.shift_name || ""} style={{ fontSize: "11px", fontWeight: "800", display: "flex", alignItems: "center", gap: "3px" }}>
                                                                        <span>F:{dayInfo.fact_hours} saat</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <style jsx="true">{`
                .search-input-wow:focus {
                    border-color: var(--primary-color) !important;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2) !important;
                    transform: translateY(-1px);
                }
                .team-row td {
                    transition: background-color 0.2s ease;
                }
                .team-row:hover td {
                    background-color: var(--inactive-btn-bg) !important;
                }
                .search-input-wow:focus {
                    border-color: var(--primary-color) !important;
                    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.15) !important;
                    background-color: var(--bg-card) !important;
                    width: 320px !important;
                }
                .search-input-wow {
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
                }
                .status-pill {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .team-row:hover .status-pill {
                    transform: scale(1.04) translateY(-2px);
                    box-shadow: 0 8px 15px rgba(0,0,0,0.12) !important;
                }
            `}</style>
        </div>
    );
}
